import React, { useMemo, useRef } from "react";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  Selection,
  Scrolling,
  Summary,
  TotalItem,
  Export,
  LoadPanel,
  ColumnChooser,
  ColumnChooserSearch,
  ColumnChooserSelection,
  Pager,
  Paging,
  StateStoring,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { api_path } from "../../shared.js";

const COLUMNS = [
  { dataField: "Job No.",                       caption: "Job No.",            width: 130, fixed: true },
  { dataField: "Shipper name",                  caption: "Shipper",            width: 180 },
  { dataField: "Consignee Name",                caption: "Consignee",          width: 180 },
  { dataField: "Agent at destination",          caption: "Agent Dest.",        width: 160 },
  { dataField: "Sold by",                       caption: "Sold by",            width: 120 },
  { dataField: "CUSTOMER PO#",                  caption: "PO#",                width: 130 },
  { dataField: "CUSTOMER INV#",                 caption: "INV#",               width: 130 },
  { dataField: "CUSTOMER REFERENCE#",           caption: "Reference#",         width: 140 },
  { dataField: "20'",                           caption: "20'",   dataType: "number", width: 60  },
  { dataField: "40'",                           caption: "40'",   dataType: "number", width: 60  },
  { dataField: "40'HC",                         caption: "40'HC", dataType: "number", width: 70  },
  { dataField: "45'HC",                         caption: "45'HC", dataType: "number", width: 70  },
  { dataField: "Number of TEU",                 caption: "TEU",   dataType: "number", width: 70  },
  { dataField: "Container Type",                caption: "Cntr Type",          width: 110 },
  { dataField: "CBM",                           caption: "CBM",   dataType: "number", width: 80  },
  { dataField: "G.W. (kg)",                     caption: "G.W.(kg)", dataType: "number", width: 90 },
  { dataField: "Mode of Main Transportation",   caption: "Mode",               width: 80  },
  { dataField: "Loading type",                  caption: "Load Type",          width: 90  },
  { dataField: "Incoterm",                      caption: "Incoterm",           width: 80  },
  { dataField: "Cargo Type",                    caption: "Cargo Type",         width: 100 },
  { dataField: "Vessel Type",                   caption: "Vessel Type",        width: 100 },
  { dataField: "Ocean Carrier / Airline",       caption: "Carrier",            width: 120 },
  { dataField: "CO-Loader",                     caption: "CO-Loader",          width: 110 },
  { dataField: "Service Contract owner",        caption: "SC Owner",           width: 120 },
  { dataField: "Booking number",                caption: "Booking No.",        width: 130 },
  { dataField: "Port of Loading",               caption: "POL",                width: 120 },
  { dataField: "Port of Discharge",             caption: "POD",                width: 120 },
  { dataField: "Place of Delivery",             caption: "Place of Del.",      width: 130 },
  { dataField: "Final Destination",             caption: "Final Dest.",        width: 130 },
  { dataField: "ETD Port of Loading",           caption: "ETD",  dataType: "date", width: 100 },
  { dataField: "ETA Port of Discharge",         caption: "ETA",  dataType: "date", width: 100 },
  { dataField: "2004 : ATD Port of Loading",    caption: "ATD",  dataType: "date", width: 100 },
  { dataField: "3002 : ATA Port of Discharge",  caption: "ATA",  dataType: "date", width: 100 },
  { dataField: "SI Cut off date",               caption: "SI Cut off", dataType: "date", width: 110 },
  { dataField: "VGM Cut off Date",              caption: "VGM Cut off", dataType: "date", width: 110 },
  { dataField: "FCL Cut off",                   caption: "CY Cut off", dataType: "date", width: 110 },
  { dataField: "MBL#",                          caption: "MBL#",               width: 140 },
  { dataField: "Bill of Lading #",              caption: "HBL#",               width: 140 },
  { dataField: "1st Vessel name",               caption: "Vessel",             width: 150 },
  { dataField: "1st Vessel Voyage",             caption: "Voyage",             width: 100 },
  { dataField: "COMMODITY",                     caption: "Commodity",          width: 160 },
  { dataField: "HS Code",                       caption: "HS Code",            width: 100 },
  { dataField: "Quantity",                      caption: "Qty",  dataType: "number", width: 80 },
  { dataField: "Unit of Quantity",              caption: "Unit",               width: 80  },
  { dataField: "Estimated Transit time",        caption: "Est. Transit (days)", dataType: "number", width: 130 },
  { dataField: "Actual Transit time",           caption: "Act. Transit (days)", dataType: "number", width: 130 },
  { dataField: "Week",                          caption: "Week", dataType: "number", width: 60  },
  { dataField: "Year",                          caption: "Year", dataType: "number", width: 70  },
  { dataField: "Invoice Total Amount (THB)",    caption: "Invoice (THB)", dataType: "number", width: 130, format: { type: "fixedPoint", precision: 2 } },
  { dataField: "Total Ocean Freight Rate (THB) in the shipment", caption: "Sell (THB)", dataType: "number", width: 110, format: { type: "fixedPoint", precision: 2 } },
  { dataField: "Ocean freight cost",            caption: "Cost (THB)",  dataType: "number", width: 110, format: { type: "fixedPoint", precision: 2 } },
  { dataField: "Remark for Report 1",           caption: "Remark 1",           width: 180 },
  { dataField: "Remark for Report 2",           caption: "Remark 2",           width: 180 },
];

export default function SalesCoReportComponent({ userId, salesId, etdFrom, etdTo }) {
  const gridRef = useRef(null);

  // ✅ KEY FIX 1: createStore key ต้องตรงกับ keyExpr บน DataGrid
  // และ key ต้องเป็น field ที่มีอยู่จริงใน response ของ API
  // ถ้า vw_sales_report ไม่มี doc_id ใน SELECT → ต้องเปลี่ยนเป็น "Job No."
  const dataSource = useMemo(() => {
    const qs = new URLSearchParams({
      ...(salesId  && { sales_id: salesId }),
      ...(etdFrom  && { etd_from: etdFrom }),
      ...(etdTo    && { etd_to:   etdTo   }),
    }).toString();

    return createStore({
      key: "Job No.",   // ✅ ต้องตรงกับ keyExpr ด้านล่าง และมีใน SELECT จริง
      loadUrl: `${api_path}/fc/getSalescoReport.php${qs ? '?' + qs : ''}`,
    });
  }, [salesId, etdFrom, etdTo]);

  return (
    <div className="p-4 bg-white shadow rounded-2xl">
      <h5 className="font-semibold mb-2">📈 Report</h5>

      <DataGrid
        ref={gridRef}
        dataSource={dataSource}
        height={800}
        columnAutoWidth={false}
        showBorders={true}
        showColumnLines={true}
        showRowLines={true}
        rowAlternationEnabled={true}

        // ✅ KEY FIX 2: keyExpr ต้องตรงกับ key ใน createStore
        keyExpr="Job No."

        allowColumnReordering={true}
        allowColumnResizing={true}
        columnResizingMode="widget"

        // ✅ KEY FIX 3: remoteOperations — PHP ต้องรองรับ take/skip/sort/filter
        remoteOperations={{ filtering: true, sorting: true, paging: true }}
      >
        <Paging defaultPageSize={50} />
        <Pager
          showPageSizeSelector={true}
          allowedPageSizes={[25, 50, 100]}
          showInfo={true}
          showNavigationButtons={true}
        />
        <Scrolling mode="standard" showScrollbar="always" />

        <FilterRow visible={true} />
        <HeaderFilter visible={true} />
        <Selection mode="single" />
        <LoadPanel enabled={true} />

        {/* StateStoring: จำ layout ของ user ไว้ใน localStorage */}
        <StateStoring
          enabled={true}
          type="localStorage"
          storageKey="salesCoReport_gridState_v2"
        />

        <Export enabled={true} allowExportSelectedData={false} />

        <ColumnChooser enabled={true} mode="select" height={400}>
          <ColumnChooserSearch enabled={true} />
          <ColumnChooserSelection allowSelectAll={true} selectByClick={true} recursive={true} />
        </ColumnChooser>

        {COLUMNS.map((col) => (
          <Column
            key={col.dataField}
            dataField={col.dataField}
            caption={col.caption}
            width={col.width}
            dataType={col.dataType}
            format={col.format}
            allowHeaderFiltering={true}
            allowFiltering={true}
            allowSorting={true}
          />
        ))}

        <Summary>
          <TotalItem column="Number of TEU"    summaryType="sum" displayFormat="TEU: {0}" />
          <TotalItem column="Invoice Total Amount (THB)" summaryType="sum" valueFormat={{ type: "fixedPoint", precision: 2 }} displayFormat="฿{0}" />
          <TotalItem column="Total Ocean Freight Rate (THB) in the shipment" summaryType="sum" valueFormat={{ type: "fixedPoint", precision: 2 }} displayFormat="฿{0}" />
          <TotalItem column="Ocean freight cost" summaryType="sum" valueFormat={{ type: "fixedPoint", precision: 2 }} displayFormat="฿{0}" />
        </Summary>
      </DataGrid>
    </div>
  );
}
